import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { listHospitalConversations } from "../api/shiftConversation";
import { ConversationListItem } from "../interface/shiftConversation";
import { useHospitalRealtime } from "../hooks/useHospitalRealtime";
import { HOSPITAL_CONVERSATION_EVENT_TYPES } from "../realtime/realtimeTypes";
import { REALTIME_POLLING_ENABLED } from "../realtime/realtimeConfig";
import {
  readShiftConversationUpdatedDetail,
  zeroUnreadForConversation,
} from "../utils/conversationUnread";
import { isDeletedDoctor } from "../utils/deletedDoctor";
const FETCH_TIMEOUT_MS = 12_000;
const FETCH_STALE_LOCK_MS = 20_000;

const getConversationFingerprint = (item: ConversationListItem) => {
  const conv = item.conversation;
  const last = item.lastMessage;
  return [
    conv?.conversationId || "",
    conv?.unreadCount ?? 0,
    conv?.status || "",
    last?.createTime ?? 0,
    last?.messageText || "",
  ].join("|");
};

const hasConversationListChanged = (
  prev: ConversationListItem[],
  next: ConversationListItem[],
) => {
  if (prev.length !== next.length) return true;

  const prevMap = new Map(
    prev.map((item) => [
      item.conversation?.conversationId,
      getConversationFingerprint(item),
    ]),
  );

  for (const item of next) {
    const id = item.conversation?.conversationId;
    if (!id) return true;
    if (prevMap.get(id) !== getConversationFingerprint(item)) {
      return true;
    }
  }

  return false;
};

export const MessagesPanel: React.FC<{
  triggerClassName?: string;
  onOpen?: () => void;
}> = ({
  triggerClassName,
  onOpen,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMessagesActive = location.pathname === "/messages";
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const isFetchingRef = useRef(false);
  const lastFetchStartedAtRef = useRef(0);
  const realtimeFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const totalUnread = conversations.reduce(
    (sum, item) => sum + (item.conversation?.unreadCount ?? 0),
    0,
  );

  const fetchList = useCallback(async () => {
    const now = Date.now();
    if (isFetchingRef.current) {
      const lockAge = now - lastFetchStartedAtRef.current;
      if (lockAge < FETCH_STALE_LOCK_MS) {
        return;
      }
      isFetchingRef.current = false;
    }

    isFetchingRef.current = true;
    lastFetchStartedAtRef.current = now;
    try {
      const timeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("messages-fetch-timeout")), FETCH_TIMEOUT_MS);
      });

      const res = await Promise.race([listHospitalConversations(), timeout]);

      if (res.code === 200 && Array.isArray(res.data)) {
        const activeOnly = res.data.filter(
          (item: ConversationListItem) => !isDeletedDoctor(item as any),
        );
        setConversations((prev) => {
          if (!hasConversationListChanged(prev, activeOnly)) {
            return prev;
          }
          return activeOnly;
        });
      }
    } catch {
      // silently ignore — network errors shouldn't break the nav
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const scheduleRealtimeFetchList = useCallback(() => {
    if (realtimeFetchTimerRef.current) {
      clearTimeout(realtimeFetchTimerRef.current);
    }
    realtimeFetchTimerRef.current = setTimeout(() => {
      realtimeFetchTimerRef.current = null;
      void fetchList();
    }, 400);
  }, [fetchList]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    return () => {
      if (realtimeFetchTimerRef.current) {
        clearTimeout(realtimeFetchTimerRef.current);
      }
    };
  }, []);

  useHospitalRealtime({
    enabled: !isMessagesActive,
    eventTypes: HOSPITAL_CONVERSATION_EVENT_TYPES,
    onInboxEvent: scheduleRealtimeFetchList,
    onFallbackPoll: fetchList,
  });

  useEffect(() => {
    const refreshOnFocus = () => fetchList();
    const refreshOnVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchList();
      }
    };
    const refreshOnConversationUpdate = (event: Event) => {
      const detail = readShiftConversationUpdatedDetail(event);
      if (detail?.conversationId && detail.clearedUnread) {
        setConversations((prev) =>
          zeroUnreadForConversation(prev, detail.conversationId!),
        );
      }
      void fetchList();
    };

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnVisibility);
    window.addEventListener(
      "shift-conversation-updated",
      refreshOnConversationUpdate,
    );

    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnVisibility);
      window.removeEventListener(
        "shift-conversation-updated",
        refreshOnConversationUpdate,
      );
    };
  }, [fetchList]);

  const badgeLabel =
    totalUnread > 99 ? "99+" : totalUnread > 0 ? String(totalUnread) : null;

  return (
    <button
      type="button"
      onClick={() => {
        if (onOpen) {
          onOpen();
          return;
        }
        if (isMessagesActive) {
          return;
        }
        flushSync(() => {
          navigate("/messages");
        });
      }}
      className={`relative flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
        isMessagesActive
          ? "bg-accent text-primary"
          : "text-muted-foreground hover:bg-white hover:text-foreground"
      } ${triggerClassName || ""}`}
      title="Messages"
      aria-label="Messages"
    >
      <MessageCircle className="w-5 h-5" />
      {badgeLabel && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
          {badgeLabel}
        </span>
      )}
    </button>
  );
};

export default MessagesPanel;
